import { createClient } from "@/lib/supabase/client"
import { getCharacterPlatformBackendUrl } from "./server"
import type { CatalogSkill } from "./skills"

export interface PlatformDashboardAgent {
  id: string
  user_id: string
  agent_id: string
  name: string | null
  model: string | null
  is_default: boolean
  updated_at: string
}

export interface PlatformDashboardAgentSkill {
  id: string
  user_id: string
  agent_id: string | null
  skill_key: string
  name: string | null
  enabled: boolean
  status: string | null
  updated_at: string
  registry_skill: CatalogSkill | null
  shared_skill: CatalogSkill | null
}

export interface PlatformDashboardResponse {
  app_user: {
    id: string
    email: string | null
    username: string | null
    full_name: string | null
    avatar_url: string | null
    platform_key: string | null
    openclaw_connected?: boolean
    roles?: string[]
  }
  agents: PlatformDashboardAgent[]
  agent_skills: PlatformDashboardAgentSkill[]
  saved_skills: CatalogSkill[]
  shared_skills: CatalogSkill[]
}

async function getAccessToken(): Promise<string | null> {
  const supabase = createClient()
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

async function parseJsonOrThrow<T>(
  response: Response,
  fallback: string
): Promise<T> {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? String((payload as { detail?: unknown }).detail ?? fallback)
        : fallback
    throw new Error(detail)
  }
  return payload as T
}

export async function fetchPlatformDashboard(
  accessToken?: string
): Promise<PlatformDashboardResponse> {
  const token = accessToken ?? (await getAccessToken())
  if (!token) {
    throw new Error("Not authenticated")
  }

  const response = await fetch(
    new URL("/v1/platform-core/dashboard", getCharacterPlatformBackendUrl()),
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: "no-store",
    }
  )

  return parseJsonOrThrow<PlatformDashboardResponse>(
    response,
    "Failed to fetch platform dashboard"
  )
}
